/**
 * Calm full-area overlay states for the Sheets (Univer) embed:
 * loading, error-with-retry, and "Sheets isn't turned on for your store yet".
 * iPad-Settings tone: one centered column, soft icon tile, short copy, one clear action.
 */
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { HugeiconsIcon } from "@hugeicons/react";
import { Alert02Icon, GridTableIcon } from "@hugeicons/core-free-icons";
import { RefreshCw } from "lucide-react";
import { AnimatedStarburst } from "@/components/icons/AnimatedStarburst";
import { Button } from "@/components/ui/button";

type Translations = Record<string, string>;

function Overlay({ children }: { children: ReactNode }) {
  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center bg-background px-6">
      <div className="flex w-full max-w-sm flex-col items-center gap-4 text-center">{children}</div>
    </div>
  );
}

function IconTile({ tone, icon }: { tone: "neutral" | "warning"; icon: typeof GridTableIcon }) {
  const toneClass =
    tone === "warning" ? "bg-amber-500/10 text-amber-600" : "bg-emerald-500/10 text-emerald-600";
  return (
    <div className={`flex h-14 w-14 items-center justify-center rounded-2xl ${toneClass}`}>
      <HugeiconsIcon icon={icon} size={28} strokeWidth={1.5} />
    </div>
  );
}

export function UniverLoadingState({ t }: { t: Translations }) {
  return (
    <Overlay>
      <AnimatedStarburst />
      <p className="text-sm text-muted-foreground">{t.loading}</p>
    </Overlay>
  );
}

export function UniverErrorState({
  t,
  message,
  onRetry,
}: {
  t: Translations;
  message: string | null;
  onRetry: () => void;
}) {
  return (
    <Overlay>
      <IconTile tone="warning" icon={Alert02Icon} />
      <div className="space-y-1.5">
        <h2 className="text-base font-semibold text-foreground">{t.errorTitle}</h2>
        <p className="text-sm leading-relaxed text-muted-foreground">{message || t.errorBody}</p>
      </div>
      <Button onClick={onRetry} className="mt-2 rounded-xl">
        <RefreshCw />
        {t.retry}
      </Button>
    </Overlay>
  );
}

function InfoState({ t, title, body }: { t: Translations; title: string; body: string }) {
  return (
    <Overlay>
      <IconTile tone="neutral" icon={GridTableIcon} />
      <div className="space-y-1.5">
        <h2 className="text-base font-semibold text-foreground">{title}</h2>
        <p className="text-sm leading-relaxed text-muted-foreground">{body}</p>
      </div>
      <Button asChild variant="outline" className="mt-2 rounded-xl">
        <Link to="/support">{t.contactSupport}</Link>
      </Button>
    </Overlay>
  );
}

/** CP answered UNIVER_NOT_ENABLED: the store hasn't been switched on for Sheets. */
export function UniverNotEnabledState({ t }: { t: Translations }) {
  return <InfoState t={t} title={t.notEnabledTitle} body={t.notEnabledBody} />;
}

/** CP answered TENANT_INACTIVE / NOT_A_MEMBER / TENANT_NOT_FOUND. */
export function UniverNoAccessState({ t }: { t: Translations }) {
  return <InfoState t={t} title={t.noAccessTitle} body={t.noAccessBody} />;
}
